/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Pagination,
  Table,
  TableBody,
  TableFooter,
  TableRow,
  TableSortLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import {
  StyledTableHeadCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableBodyCell,
  StyledTypography,
  StyledTableRow,
  StyledSelect,
  StyledPerPageContainer,
  StyledPaginationContainer,
  ExportContainer,
  ColumnInfoTooltip,
} from "./styles";
import { useTranslation } from "react-i18next";
import DownloadIcon from '@mui/icons-material/Download';
import { StyledInfoIcon } from '../statistics_cards/styles';
import csvLoader from '../../assets/images/csv2.gif'
import { scrollbarStyles } from '../../pages/reports/styles'
import { StyledMenuItem } from '../styled_menu_item/index'
import { useFillViewportHeight } from '../../hooks/useFillViewportHeight'

// Columns opt in with `show_info` + `description`. The icon sits outside TableSortLabel so using it never sorts.
const withColumnInfo = (col, header) => {
  if (!col.show_info || !col.description) return header;

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {/* describeChild makes the text the button's description; its aria-label stays the name. */}
      <ColumnInfoTooltip title={col.description} placement="top" describeChild>
        <IconButton
          size="small"
          disableRipple
          aria-label={`About ${col.label}`}
          data-testid={`column-info-${col.key}`}
          // The icon is 1em, so the button's font size sets its size.
          sx={{ p: 0, fontSize: "14px" }}
        >
          <StyledInfoIcon />
        </IconButton>
      </ColumnInfoTooltip>
      {header}
    </Box>
  );
};

export const IndexTable = ({
  report = true,
  columns,
  headerRows,
  data,
  type = "entries",
  tableId,
  itemsPerPage,
  setItemsPerPage,
  page,
  setPage,
  pageCount,
  checkboxes = false,
  selectedIds = [],
  setSelectedIds,
  fetchingLoader,
  hideNavigation,
  searchLoading = false,
  hasSearchTerm = false,
  emptyStateText = "No Data Exists",
  enableRowClick = false,
  handleOpen,
  setSelectedRow,
  showDownloadButton = false,
  disableDownloadButton = true,
  downloadButtonLabel = "Download CSV",
  handleExportCsv,
  csvLoading,
  checkboxWidth,
  serverSort = false,
  sortMode = "client",
  sortBy,
  sortOrder,
  onSortChange,
  defaultSortBy = "",
  defaultSortOrder = "asc",
  serverOrderBy = "",
  serverOrder = "asc",
  onSort,
  disableColumnResize = false,
  getRowProps,
  footerRows = [],
  fillViewportHeight = true,
  maxHeight,
}) => {
  const activeSortMode = sortMode === "server" || serverSort ? "server" : "client";
  const activeSortBy = sortBy ?? serverOrderBy ?? defaultSortBy;
  const activeSortOrder = sortOrder ?? serverOrder ?? defaultSortOrder;

  const [orderBy, setOrderBy] = useState(activeSortBy);
  const [order, setOrder] = useState(activeSortOrder);
  const [scrollWidth, setScrollWidth] = useState(0);
  const tableRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const tableFooterRef = useRef(null);

  const availableHeight = useFillViewportHeight({
    containerRef: tableRef,
    bottomRef: tableFooterRef,
    enabled: fillViewportHeight && maxHeight === undefined,
  });
  const containerMaxHeight = maxHeight ?? availableHeight;

  const { t } = useTranslation();
  const storageKey = `tableWidths-${tableId || type || "default"}`;

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) return JSON.parse(saved);
    return columns.reduce((acc, col) => {
      acc[col.key] = col.width || 150;
      return acc;
    }, {});
  });

  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  // Handles column resizing
  const startResizing = (key, e) => {
    e.preventDefault();
    resizingRef.current = { columnKey: key, startX: e.clientX, startWidth: columnWidths[key] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const columnDef = columns.find(c => c.key === columnKey);
    const minWidth = columnDef?.minWidth || 80; // default to 80 if not specified
    const newWidth = Math.max(minWidth, startWidth + e.clientX - startX);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, [columns]);

  const handleMouseUp = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const columnDef = columns.find(c => c.key === columnKey);
    const minWidth = columnDef?.minWidth || 80;
    const finalWidth = Math.max(minWidth, startWidth + e.clientX - startX);

    localStorage.setItem(storageKey, JSON.stringify({ ...columnWidths, [columnKey]: finalWidth }));
    setColumnWidths((prev) => ({ ...prev, [columnKey]: finalWidth }));

    resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [columnWidths, storageKey, handleMouseMove]);

  const handleRowSelect = (row) => {
    if (enableRowClick) {
      setSelectedRow(row);
      handleOpen();
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selectedIds.indexOf(id);
    const newSelected = selectedIndex === -1
      ? [...selectedIds, id]
      : [...selectedIds.slice(0, selectedIndex), ...selectedIds.slice(selectedIndex + 1)];
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === visibleRows?.length) setSelectedIds([]);
    else setSelectedIds(visibleRows?.map((row) => row.id));
  };

  const handleSort = (property) => {
    if (property.sortable === false) return;

    if (activeSortMode === "server") {
      const currentOrderBy = orderBy || activeSortBy || "";
      const currentOrder = order || activeSortOrder || "asc";
      const nextOrder = currentOrderBy === property.key && currentOrder === "asc" ? "desc" : "asc";

      setOrderBy(property.key);
      setOrder(nextOrder);
      onSortChange?.(property.key, nextOrder);
      onSort?.(property.key, nextOrder);
      return;
    }

    const isAsc = orderBy === property.key && order === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(property.key);

    if (serverSort && typeof onSortChange === "function") {
      onSortChange(property.key, nextOrder);
    }
  };

  // Handles horizontal scrollbar
  const handleMainScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
  };

  const handleFakeScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
  };

  useEffect(() => {
    if (tableRef.current) {
      setScrollWidth(tableRef.current.scrollWidth);
    }
  }, [columnWidths, data]);

  useEffect(() => {
    const onResize = () => {
      if (tableRef.current) {
        setScrollWidth(tableRef.current.scrollWidth);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const descendingComparator = (a, b, orderBy) => {
    const getSortValue = (value) => {
      if (value && typeof value === "object" && value.sortValue !== undefined) return value.sortValue;
      if (value && typeof value === "string" && value.sortValue !== undefined) return value.sortValue.toLowerCase();
      if (value && typeof value === "string") return value.toLowerCase();
      return value;
    };
    const aValue = getSortValue(a[orderBy]);
    const bValue = getSortValue(b[orderBy]);
    const isNumeric = (string) => /^[+-/$]?\d+(\.\d+)?$/.test(string);
    if (isNumeric(aValue) && isNumeric(bValue)) {
      if (aValue[0] === "$" && bValue[0] === "$") return Number(bValue.substring(1)) - Number(aValue.substring(1));
      return bValue - aValue;
    }
    if ((bValue || "") < (aValue || "")) return -1;
    if ((bValue || "") > (aValue || "")) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => (order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy));

  const currentOrder = activeSortMode === "server" ? (order || activeSortOrder || "asc") : order;
  const effectiveOrderBy = activeSortMode === "server" ? (orderBy || activeSortBy || "") : orderBy;

  const visibleRows = useMemo(() => {
    if (!data?.length) return [];
    if (activeSortMode === "server") return data;
    return [...data].sort(getComparator(order, orderBy));
  }, [data, activeSortMode, order, orderBy]);

  const totalCount = Math.ceil(pageCount / itemsPerPage);
  const from = totalCount === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const to = Math.min(page * itemsPerPage, pageCount);

  const isDiscountCardsPath =
    /^\/admin\/perq_cards\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/create\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/edit\/\d+\/?$/.test(location.pathname);

  const pulsingText = {
    textAlign: "center",
    animation: "pulse 1.5s ease-in-out infinite",
    "@keyframes pulse": { "0%": { opacity: 1 }, "50%": { opacity: 0.5 }, "100%": { opacity: 1 } },
  };

  const dataCellSx = (col) => ({
    width: columnWidths[col.key],
    minWidth: col.minWidth || columnWidths[col.key],
    maxWidth: columnWidths[col.key],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...(col.cellSx || {}),
  });

  return (
    <Box sx={{ position: "relative", marginRight: report ? "30px" : 0 }} data-testid="index-table">
      <StyledPerPageContainer>
        <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {!hideNavigation && (
            <>
              <StyledSelect
                value={itemsPerPage}
                size="small"
                inputProps={{ "aria-label": `${type} per page` }}
                onChange={(e) => {
                  setItemsPerPage(e.target.value);
                  setPage(1);
                }}
                data-testid="index-table-select"
              >
                {[10, 25, 50, 100].map((num) => (
                  <StyledMenuItem key={num} value={num} sx={{ fontSize: "0.875rem" }} data-testid={`menu-item-${num}`}>
                    {num}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
              <StyledTypography variant="body2">
                {`${type} ${isDiscountCardsPath ? t("discount-cards.index.table.per-page") : "per page"}`}
              </StyledTypography>
            </>
          )}
        </Box>
        {(!hideNavigation || showDownloadButton) && (
          <ExportContainer>
            {!hideNavigation && (
              <StyledTypography variant="body2">
                {`${isDiscountCardsPath ? t("discount-cards.index.table.showing") : "Showing"} ${from} ${
                  isDiscountCardsPath ? t("discount-cards.index.table.to") : "to"
                } ${to} ${isDiscountCardsPath ? t("discount-cards.index.table.of") : "of"} ${pageCount} ${type}`}
              </StyledTypography>
            )}
            {
              showDownloadButton && (
                csvLoading ? (
                  <IconButton disableRipple>
                    <img style={{ height: 24 }} src={csvLoader} alt="loading..." />
                  </IconButton>
                ) : (
                  <IconButton
                    data-testid="export-csv-button"
                    disabled={disableDownloadButton}
                    onClick={handleExportCsv}
                    aria-label={downloadButtonLabel}
                    sx={{ color: '#2b4fb9' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                )
              )
            }
          </ExportContainer>
        )}
      </StyledPerPageContainer>

      <StyledTableContainer
        ref={tableRef}
        onScroll={handleMainScroll}
        sx={{
          mt: 1,
          maxHeight: containerMaxHeight,
        }}
      >
        <Table>
          <StyledTableHead report={report}>
            {(headerRows || []).map((headerRow, rowIndex) => (
              <TableRow key={`header-row-${rowIndex}`}>
                {checkboxes && <StyledTableHeadCell aria-hidden sx={checkboxWidth ? { width: checkboxWidth } : undefined} />}
                {headerRow.map((cell, cellIndex) => (
                  <StyledTableHeadCell
                    key={cell.key ?? cellIndex}
                    colSpan={cell.colSpan || 1}
                    rowSpan={cell.rowSpan || 1}
                    align={cell.align || "left"}
                    sx={cell.sx}
                  >
                    {cell.label}
                  </StyledTableHeadCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              {checkboxes && (
                <StyledTableHeadCell sx={checkboxWidth ? { width: checkboxWidth } : undefined}>
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < visibleRows?.length}
                    checked={visibleRows?.length > 0 && selectedIds.length === visibleRows?.length}
                    onChange={handleSelectAll}
                  />
                </StyledTableHeadCell>
              )}
              {columns.map((col, index) => (
                <StyledTableHeadCell
                  key={index}
                  align={col.align}
                  sortDirection={orderBy === col.key ? order : false}
                  sx={{ width: columnWidths[col.key], minWidth: 80, position: "relative", userSelect: "none", ...(col.headerSx || {}) }}
                >
                  {withColumnInfo(
                    col,
                    col.sortable !== false ? (
                      <TableSortLabel active={effectiveOrderBy === col.key} direction={effectiveOrderBy === col.key ? currentOrder : "asc"} onClick={() => handleSort(col)}>
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      <span style={{ cursor: "default" }}>{col.label}</span>
                    )
                  )}
                  {!disableColumnResize && (
                    <Box
                      onMouseDown={(e) => startResizing(col.key, e)}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "6px",
                        cursor: "col-resize",
                        zIndex: 10,
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                      }}
                    />
                  )}
                </StyledTableHeadCell>
              ))}
            </TableRow>
          </StyledTableHead>

          <TableBody>
            {searchLoading && hasSearchTerm ? (
              <StyledTableRow report={report}>
                <StyledTableBodyCell
                  colSpan={columns.length + (checkboxes ? 1 : 0)}
                  role="status"
                  aria-live="polite"
                >
                  <StyledTypography variant="body2" sx={pulsingText}>
                    Searching...
                  </StyledTypography>
                </StyledTableBodyCell>
              </StyledTableRow>
            ) : visibleRows.length === 0 ? (
              <StyledTableRow report={report}>
                <StyledTableBodyCell colSpan={columns.length + (checkboxes ? 1 : 0)}>
                  {searchLoading && hasSearchTerm ? (
                    <StyledTypography variant="body2" sx={pulsingText}>
                      Searching...
                    </StyledTypography>
                  ) : (
                    <StyledTypography variant="body2">{emptyStateText}</StyledTypography>
                  )}
                </StyledTableBodyCell>
              </StyledTableRow>
            ) : (
              visibleRows?.map((item, index) => (
                <StyledTableRow
                  report={report}
                  key={index}
                  hover={checkboxes}
                  selected={checkboxes && selectedIds.includes(item.id)}
                  onClick={() => handleRowSelect(item)}
                  {...(getRowProps?.(item, index) || {})}
                >
                  {checkboxes && (
                    <StyledTableBodyCell report={report} sx={checkboxWidth ? { width: checkboxWidth } : undefined}>
                      <Checkbox checked={selectedIds.includes(item.id)} onChange={() => handleSelect(item.id)} />
                    </StyledTableBodyCell>
                  )}
                  {columns.map((col, index) => (
                    <StyledTableBodyCell
                      report={report}
                      key={index}
                      align={col.align}
                      sx={dataCellSx(col)}
                    >
                      {typeof item[col.key] === "object" && item[col.key]?.display !== undefined ? item[col.key].display : item[col.key]}
                    </StyledTableBodyCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>

          {footerRows?.length > 0 && (
            <TableFooter data-testid="index-table-footer">
              {footerRows.map((footerRow, rowIndex) => (
                <TableRow
                  key={footerRow.key ?? `footer-row-${rowIndex}`}
                  data-testid="index-table-footer-row"
                  sx={footerRow.sx}
                >
                  {/* Empty gutter matching the body's checkbox column, so cells stay aligned. */}
                  {checkboxes && (
                    <StyledTableBodyCell
                      report={report}
                      variant="body"
                      sx={checkboxWidth ? { width: checkboxWidth } : undefined}
                    />
                  )}
                  {columns.map((col, index) => (
                    <StyledTableBodyCell
                      report={report}
                      variant="body"
                      key={index}
                      align={col.align}
                      sx={dataCellSx(col)}
                    >
                      {typeof footerRow[col.key] === "object" && footerRow[col.key]?.display !== undefined ? footerRow[col.key].display : footerRow[col.key]}
                    </StyledTableBodyCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
      </StyledTableContainer>

      {/* Measured block below the table; its height is reserved from the available space. */}
      <Box ref={tableFooterRef} sx={{ display: 'flow-root' }}>
        <Box
          ref={fakeScrollRef}
          onScroll={handleFakeScroll}
          sx={scrollbarStyles}
          data-testid="index-table-fake-scrollbar"
        >
          <Box
            sx={{
              width: scrollWidth || '100%',
              height: "1px",
            }}
          />
        </Box>

        {!hideNavigation && (
          <StyledPaginationContainer>
            <Pagination page={page} count={totalCount} onChange={(_event, page) => setPage(page)} data-testid="index-table-pagination" />
          </StyledPaginationContainer>
        )}
      </Box>
    </Box>
  );
};
